import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { HiOutlineFolderPlus, HiOutlinePhoto } from 'react-icons/hi2';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from './ui/PageHeader';

const EditBlogCat = () => {
  const baseUrl = import.meta.env.VITE_LOCAL_BASE_URL;
  const { blog_id } = useParams();
  const [currentImage, setCurrentImage] = useState('');
  const [image, setImage] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [data, setData] = useState({
    name: '',
  });

  const errors = useMemo(
    () => ({
      name: !data.name.trim() ? 'Category name is required.' : '',
    }),
    [data.name],
  );

  useEffect(() => {
    const getSingleBlog = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/v1/blog-categories/single/${blog_id}`);
        setData({ name: response.data.data.name || '' });
        setCurrentImage(response.data.data.image || '');
      } catch (error) {
        toast.error('Category details could not be loaded.');
      }
    };

    getSingleBlog();
  }, [baseUrl, blog_id]);

  const formHandler = (event) => {
    const { name, value } = event.target;
    setData((currentData) => ({ ...currentData, [name]: value }));
  };

  const imgHandler = (event) => {
    const file = event.target.files[0];
    setImage(file || null);
  };

  const btnHandler = async (event) => {
    event.preventDefault();
    setSubmitted(true);

    if (errors.name) {
      toast.error('Please complete the highlighted fields.');
      return;
    }

    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append('name', data.name);

      if (image) {
        formData.append('image', image);
      }

      const res = await axios.put(`${baseUrl}/api/v1/blog-categories/${blog_id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(res.data.message);
      setImage(null);
      setSubmitted(false);
    } catch (err) {
      toast.error('Category could not be updated. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="page-stack">
      <PageHeader
        eyebrow="Blog Management"
        title="Edit Category"
        description="Update the category name or replace its image."
        actions={
          <Link to="/dashboard/blog-category-list" className="btn btn-soft-secondary">
            Back to Categories
          </Link>
        }
      />

      <div className="surface-card form-card">
        <form method="post" onSubmit={btnHandler} noValidate>
          <div className="row g-4">
            <div className="col-12 col-lg-4">
              {currentImage && (
                <div className="current-image-preview">
                  <img src={currentImage} alt={data.name || 'Category'} />
                </div>
              )}
              <label className="upload-dropzone" htmlFor="category-image">
                <span className="upload-icon">
                  <HiOutlinePhoto aria-hidden="true" />
                </span>
                <strong>{image ? image.name : 'Replace category image'}</strong>
                <small>Leave empty to keep the current image.</small>
              </label>
              <input
                type="file"
                name="image"
                id="category-image"
                className="visually-hidden"
                accept="image/*"
                onChange={imgHandler}
              />
            </div>

            <div className="col-12 col-lg-8">
              <div className="mb-4">
                <label className="form-label" htmlFor="category-name">
                  Category name
                </label>
                <input
                  type="text"
                  name="name"
                  id="category-name"
                  placeholder="Enter category name"
                  className={`form-control form-control-lg ${submitted && errors.name ? 'is-invalid' : ''}`}
                  value={data.name}
                  onChange={formHandler}
                />
                {submitted && errors.name && <div className="invalid-feedback">{errors.name}</div>}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
                      Updating
                    </>
                  ) : (
                    <>
                      <HiOutlineFolderPlus aria-hidden="true" />
                      Update Category
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
};

export default EditBlogCat;
